import {create} from 'zustand'

import {device} from '#/storage'
import {
  type PrivateFollowRequest,
  type PrivateProfileState,
  type PrivateStoryState,
  type PrivateRequestState,
} from './privateTypes'

type PrivateStoreState = {
  profiles: Record<string, PrivateProfileState>
  storyRings: Record<string, PrivateStoryState>
  requests: Record<string, PrivateFollowRequest[]>
  setProfileState: (did: string, state: Partial<PrivateProfileState>) => void
  setRequestState: (
    did: string,
    requestState: PrivateRequestState,
    requestId?: string,
  ) => void
  setStoryRing: (did: string, count: number) => void
  setRequests: (ownerDid: string, items: PrivateFollowRequest[]) => void
}

const PROFILE_CACHE_KEY = 'privateAccountCache'
const STORY_CACHE_KEY = 'privateStoryCache'
const REQUEST_CACHE_KEY = 'privateRequestsCache'

function loadCache<T>(key: string) {
  const stored = device.get([key as never]) as T | undefined
  if (!stored || typeof stored !== 'object') return undefined
  return stored
}

function saveCache<T>(key: string, value: T) {
  device.set([key as never], value as never)
}

const initialProfiles =
  loadCache<Record<string, PrivateProfileState>>(PROFILE_CACHE_KEY) || {}
const initialStories =
  loadCache<Record<string, PrivateStoryState>>(STORY_CACHE_KEY) || {}
const initialRequests =
  loadCache<Record<string, PrivateFollowRequest[]>>(REQUEST_CACHE_KEY) || {}

export const usePrivateStore = create<PrivateStoreState>(set => ({
  profiles: initialProfiles,
  storyRings: initialStories,
  requests: initialRequests,
  setProfileState: (did, state) => {
    set(current => {
      const next = {
        ...current.profiles,
        [did]: {
          isPrivate: state.isPrivate ?? current.profiles[did]?.isPrivate ?? false,
          approved: state.approved ?? current.profiles[did]?.approved ?? false,
          requestState:
            state.requestState ??
            current.profiles[did]?.requestState ??
            'none',
          requestId: state.requestId ?? current.profiles[did]?.requestId,
          updatedAt: Date.now(),
        },
      }
      saveCache(PROFILE_CACHE_KEY, next)
      return {profiles: next}
    })
  },
  setRequestState: (did, requestState, requestId) => {
    set(current => {
      const next = {
        ...current.profiles,
        [did]: {
          isPrivate: current.profiles[did]?.isPrivate ?? false,
          approved: requestState === 'approved',
          requestState,
          requestId: requestId ?? current.profiles[did]?.requestId,
          updatedAt: Date.now(),
        },
      }
      saveCache(PROFILE_CACHE_KEY, next)
      return {profiles: next}
    })
  },
  setStoryRing: (did, count) => {
    set(current => {
      const next = {
        ...current.storyRings,
        [did]: {count, updatedAt: Date.now()},
      }
      saveCache(STORY_CACHE_KEY, next)
      return {storyRings: next}
    })
  },
  setRequests: (ownerDid, items) => {
    set(current => {
      const next = {...current.requests, [ownerDid]: items}
      saveCache(REQUEST_CACHE_KEY, next)
      return {requests: next}
    })
  },
}))
